import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useState } from "react"
import { Alert, Button } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { useMutation } from "react-query"
import { Redirect } from "react-router"
import { Link } from "react-router-dom"
import Accordion from "../../common/accordion/accordion"
import Icon from "../../common/icon/icon.component"
import SideBar from "../../common/layout/sidebar.component"
import PaymentAccountLocationSelect from "../../common/select/PaymentAccountLocationSelect"
import LoadingSpinner from "../../common/spinner/loading.component"
import { GlobalHelper } from "../../indexImports"
import PaymentAccountService from "../../payment-account/service/paymentAccountService"
import IntlProps from "../../state/helpers/IntlProps"
import { INTEGRATION_KEY } from "../../state/integration/Constants"
import { ACCOUNT_STATUS } from "../../utils/account"
import "../merchant-onboarding/styles/styles.scss"
import { useGetLocations } from "../useGetLocations"
import { useGetPaymentAccounts } from "../usePaymentAccountSession"
import MCCSelect from "./MCCSelect"
import { CreatePaymentAccountPayload, isFormValid } from "./PaymentAccountPayload"
import { messages } from "./messages"



const MerchantOnboardingPage = (props: IntlProps) => {
    // const [agreementAccepted, setAgreementAccepted] = useState<boolean>(false)
    const [merchantForm, setMerchantForm] = useState<CreatePaymentAccountPayload>({
        merchantName: AppContext.getAccount().attributes.companyName,
        mcc: 0,
        locationIDs: [],
    })
    const [success, setSuccess] = useState<boolean | null>(null)
    const [errorMessage, setErrorMessage] = useState<string>("")

    SideBar.activateMenu("payment-processing")

    const faqLines = [...Array(6).keys()].map(i => (
        {
            header: props.intl.formatMessage(messages[`faqQuestion${i + 1}`]),
            content: props.intl.formatMessage(messages[`faqAnswer${i + 1}`])
        }
    ))


    const { paymentAccounts, isLoading: isLoadingPaymentAccounts } = useGetPaymentAccounts()
    const { locations, isLoadingLocations, availableLocations } = useGetLocations(paymentAccounts)

    const isEligibleAccount = [ACCOUNT_STATUS.ACTIVE, ACCOUNT_STATUS.DEMO].includes(AppContext.getAccount().attributes.status)
    const locationsError =
        <div className="pr-3">
            <Alert variant={"warning"}>
                <FormattedMessage
                    id="merchant.onboarding_location_error_message"
                    defaultMessage="To create a QM payment, at least one unassigned location is required.
            Before creating your account, please create a new location or unassign one from an existing QM payment account." />
            </Alert>
        </div>
    const {
        mutateAsync: createPaymentAccount,
        data: paymentAccount,
        isLoading: isSubmitting,
    } = useMutation(
        (merchantForm: CreatePaymentAccountPayload) => {
            setErrorMessage("")
            //might need to load the integration key dynamically later
            return PaymentAccountService.createPaymentAccount(INTEGRATION_KEY.RAINFOREST, merchantForm)
        },
        {
            onSuccess: () => {
                setSuccess(true)
            },
            onError: () => {
                setSuccess(false)
            }
        },
    )
    const {
        mutateAsync: createTestPaymentAccount,
    } = useMutation(
        () => {
            setErrorMessage("")
            return PaymentAccountService.createTestPaymentAccount()
        },
        {
            onSuccess: () => {
                window.location.reload()
            },
            onError: (err: Error) => {
                setErrorMessage(err.message)
                setSuccess(false)
            }
        },
    )

    const upgradeSubscriptionLink = <Link to={"/my-account?utm_source=qm_payments"}>
        {props.intl.formatMessage(messages["trialUpgradeSubscription"])}
    </Link>

    if (success && paymentAccount && !(paymentAccount instanceof Error)) {
        if (paymentAccount.hasTestProdMerchantId()) {
            window.location.reload()
            window.location.href = "/payment-processing/accounts"
            return <></>
        }

        return <Redirect to={`/payment-accounts/${paymentAccount.getId()}/edit`} />
    }

    return <div className="mb-5">
        <h2 className="m-0 custom-lead">
            {props.intl.formatMessage(messages["pageHeader"])}
        </h2>
        <div className="separator mt-2 mb-3 w-100" />
        <div className="col-12 ui-module padding-80 d-flex">
            <div className="w-75">
                <h3 className="mb-3 work-sans-font">
                    {props.intl.formatMessage(messages["activateSectionHeader"])}
                </h3>
                <ul className="mb-3 pl-3">
                    <li>
                        <p className="m-0 grey">
                            {props.intl.formatMessage(messages["activateSectionListItem1"])}
                        </p>
                    </li>
                    <li>
                        <p className="m-0 grey">
                            {props.intl.formatMessage(messages["activateSectionListItem2"])}
                        </p>
                    </li>
                </ul>
                {/* hidden until the agreement page is done */}
                {/* <div className="custom-control custom-checkbox">
            <input
                name="agreement"
                type="checkbox"
                id="agreement"
                className="custom-control-input"
                checked={agreementAccepted}
                onChange={() => setAgreementAccepted(!agreementAccepted)}
            />
            <label className="custom-control-label grey" htmlFor="agreement">
                {props.intl.formatMessage(messages["activateSectionCheckboxLabel"])}
                <a
                    className="ml-1 grey"
                    href="https://www.quotemachine.com/en/terms-of-use/"
                    target="_blank"
                    rel="external noreferrer"
                >
                    {props.intl.formatMessage(messages["activateSectionAgreement"])}
                </a>
            </label>
        </div> */}
                {availableLocations.length > 0 ? <>
                    <div className="row d-flex align-items-end mb-3">
                        {
                            isEligibleAccount && <>
                                <div className="col-4">
                                    <label className="custom-form-label mb-1" htmlFor="merchantName">
                                        {props.intl.formatMessage(messages["accountNameLabel"])}
                                    </label>
                                    <input
                                        name="merchantName"
                                        value={merchantForm.merchantName}
                                        onChange={(updatedName) => {
                                            setMerchantForm({
                                                ...merchantForm,
                                                merchantName: updatedName.target.value
                                            })
                                        }}
                                        id="merchantName"
                                        type="text"
                                        className="form-control"
                                        placeholder={props.intl.formatMessage(messages["accountNamePlaceholder"])}
                                    />
                                </div>
                                <div className="col-4">
                                    <label className="custom-form-label mb-1" htmlFor="merchantName">
                                        {props.intl.formatMessage(messages["mccLabel"])}
                                    </label>
                                    <MCCSelect
                                        selectedMcc={merchantForm.mcc}
                                        onSelect={(selectedMcc: number) => {
                                            setMerchantForm({
                                                ...merchantForm,
                                                mcc: selectedMcc
                                            })
                                        }} />
                                </div>
                            </>
                        }
                        <div className="col-4">
                            <Button
                                variant="primary"
                                size="lg"
                                className="mt-3"
                                disabled={!isEligibleAccount || !isFormValid(merchantForm)}
                                onClick={() => { createPaymentAccount(merchantForm) }}
                            >
                                {isSubmitting ? <LoadingSpinner /> : props.intl.formatMessage(messages["activateSectionButton"])}
                                {!isEligibleAccount && <Icon value="lock" className="lock" />}
                            </Button>
                        </div>
                    </div>

                    {
                        isEligibleAccount &&
                        <div className="row d-flex align-items-end">
                            <div className="col-8">
                                <label className="custom-form-label mb-1" htmlFor="merchantName">
                                    {props.intl.formatMessage(messages["assignLocations"])}
                                </label>
                                <PaymentAccountLocationSelect
                                    paymentAccounts={paymentAccounts}
                                    locations={locations}
                                    availableLocations={availableLocations}
                                    isLoading={isLoadingLocations && isLoadingPaymentAccounts}
                                    selectedLocations={merchantForm.locationIDs}
                                    onSelect={(selectedlocationsIDs) => {
                                        const locationsIDs = selectedlocationsIDs.map((option) => {
                                            if (!option.isDisabled) {
                                                return option.value
                                            }
                                            return null
                                        })
                                        setMerchantForm({
                                            ...merchantForm,
                                            locationIDs: locationsIDs.filter((id) => id !== null)
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    }

                    {
                        !isEligibleAccount &&
                        (GlobalHelper.isPaymentGatewayTestMode() ?
                            <div className="row d-flex align-items-end mb-3">
                                <div className="col-9">
                                    <p className="mb-2 small">
                                        <FormattedMessage
                                            {...messages["trialTestModeInfo"]}
                                            values={{
                                                upgrade_link: upgradeSubscriptionLink
                                            }}
                                        />
                                    </p>
                                    <p className="mb-0 small font-weight-bold">
                                        <FormattedMessage
                                            {...messages["testModeInfo"]}
                                        />
                                    </p>
                                </div>
                            </div>
                            :
                            <div className="row d-flex align-items-end mb-3">
                                <div className="col-9">
                                    {!GlobalHelper.hasShopifyIntegration() &&
                                        <p className="mb-2 small">
                                            <FormattedMessage
                                                {...messages["trialTestModeActivateInfo1"]}
                                                values={{
                                                    link: <a href="#" onClick={() => { createTestPaymentAccount() }}>
                                                        {props.intl.formatMessage(messages["testMode"])}
                                                    </a>
                                                }}
                                            />
                                        </p>
                                    }
                                    <p className="mb-0 small">
                                        <FormattedMessage
                                            {...messages["trialTestModeActivateInfo2"]}
                                            values={{
                                                upgrade_link: upgradeSubscriptionLink
                                            }}
                                        />
                                    </p>
                                </div>
                            </div>
                        )
                    }
                </>
                    :
                    <>
                        {locationsError}
                    </>
                }
                {errorMessage &&
                    <div className="row mt-2 d-flex">
                        <div className="offset-4" />
                        <div className="col-8">
                            <span className="text-danger">{errorMessage}</span>
                        </div>
                    </div>}
            </div>
            <div className="d-flex flex-column align-items-center w-25">
                <h4 className="work-sans-font">
                    {props.intl.formatMessage(messages["feesSectionHeader"])}
                </h4>
                <div className="fees-card d-flex flex-column justify-content-center align-items-center w-100 h-100 p-2">
                    <p className="mb-0 small">
                        {props.intl.formatMessage(messages["feesSectionCreditCardPresent"])}
                    </p>
                    <p className="mb-3">
                        <span className="large bold">2.60%</span><span className="small bold ml-1">+ 0.20ct</span>
                    </p>
                    <p className="mb-0 small">
                        {props.intl.formatMessage(messages["feesSectionCreditCardNotPresent"])}
                    </p>
                    <p className="mb-3">
                        <span className="large bold">2.90%</span><span className="small bold ml-1">+ 0.20ct</span>
                    </p>
                    <p className="mb-0 extra-small">
                        {props.intl.formatMessage(messages["feesSectionAmex"])}
                    </p>
                    <p className="mb-3">
                        <span className="medium bold">0.5%</span>
                    </p>
                    <p className="mb-0 small">
                        {props.intl.formatMessage(messages["feesSectionAch"])}
                    </p>
                    <p className="mb-0">
                        <span className="large bold">1%</span><span className="small bold ml-1">+ 0.50ct</span>
                    </p>
                </div>
            </div>
        </div>
        <h2 className="mb-3 mt-3 custom-lead">
            {props.intl.formatMessage(messages["faqHeader"])}
        </h2>
        <Accordion lines={faqLines} />
    </div >
}

export default injectIntl(MerchantOnboardingPage)


// WEBPACK FOOTER //
// ./src/payment-processing/merchant-onboarding/MerchantOnboardingPage.tsx